/** 30-day sessions-over-time line chart — SVG, no dep.
 *
 * Accepts the `sessions_timeseries` array returned by
 * `/v1/dashboard/stats` (one bucket per UTC day). Renders a filled
 * gradient area + a stroke line on top, with x-axis labels at the
 * first / middle / last bucket and a y-axis tick at the max value.
 * No interactivity at v1 — kept tiny on purpose; tooltips are a v1.2
 * follow-up.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/lib/format";


export type TimeseriesPoint = { date: string; count: number };


interface Props {
  data: TimeseriesPoint[];
  height?: number;
  /** ARIA label for screen readers. */
  label?: string;
}


export function SessionsLineChart({
  data,
  height = 160,
  label,
}: Props) {
  const { t } = useTranslation();
  const ariaLabel = label ?? t("ui.sessionsLineChart.defaultLabel");
  // Viewbox uses the data length as the X resolution + the max as the
  // Y. That way the SVG scales correctly via preserveAspectRatio while
  // keeping the geometry crisp at any container width.
  const W = Math.max(data.length - 1, 1);
  const H = 100;

  const { line, area, maxValue, midIndex } = useMemo(() => {
    if (data.length === 0) {
      return { line: "", area: "", maxValue: 0, midIndex: 0 };
    }
    const max = Math.max(...data.map((d) => d.count), 1);
    const xy = data.map((d, i) => {
      const x = i;
      const y = H - (d.count / max) * (H - 6) - 2;
      return [x, y] as const;
    });
    const linePath = xy
      .map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`))
      .join(" ");
    const areaPath =
      xy
        .map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`))
        .join(" ") + ` L${W},${H} L0,${H} Z`;
    return {
      line: linePath,
      area: areaPath,
      maxValue: max,
      midIndex: Math.floor(data.length / 2),
    };
  }, [data, W]);

  if (data.length === 0) {
    return (
      <div className="text-sm text-nb-text-3">{t("ui.sessionsLineChart.noData")}</div>
    );
  }

  // Pre-pick label dates for the x-axis (3 ticks total). The early
  // return above guarantees data is non-empty; the `?.date ?? ""`
  // shapes keep strict-noUncheckedIndexedAccess happy.
  const firstLabel = shortDate(data[0]?.date ?? "");
  const midLabel = shortDate(data[midIndex]?.date ?? "");
  const lastLabel = shortDate(data[data.length - 1]?.date ?? "");

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-xs text-nb-text-3">
        <span>0</span>
        <span className="font-mono text-nb-text">{t("ui.sessionsLineChart.max", { value: maxValue })}</span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        style={{ width: "100%", height: `${height}px` }}
      >
        <defs>
          <linearGradient id="sessions-line-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(14 165 233)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="rgb(14 165 233)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#sessions-line-grad)" />
        <path
          d={line}
          fill="none"
          stroke="rgb(14 165 233)"
          strokeWidth="1.2"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="flex items-baseline justify-between text-[10px] text-nb-text-3">
        <span>{firstLabel}</span>
        <span>{midLabel}</span>
        <span>{lastLabel}</span>
      </div>
    </div>
  );
}


function shortDate(iso: string): string {
  // "2026-05-22" → localised "22 mai" / "May 22". Pin the tz to UTC: the
  // value is a calendar-date bucket, so the org tz must NOT shift the day.
  return formatDate(iso + "T00:00:00Z", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
